import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import API_URL, { parseJsonResponse } from '../api';
import { compressImageFile } from '../utils/imageHelpers';
import { getShiftLabel } from '../constants/shiftOptions';
import EmployeeQRCode from './EmployeeQRCode';

const CameraIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

function ProfileField({ label, value }) {
  return (
    <div className="employee-profile-field">
      <span className="employee-profile-field-label">{label}</span>
      <span className="employee-profile-field-value">{value || '—'}</span>
    </div>
  );
}

function EmployeeProfile({ user, employee, apiUrl = API_URL, onPhotoUpdated, open = false, onClose }) {
  const [photo, setPhoto] = useState(employee?.photo || '');
  const [isUploading, setIsUploading] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    setPhoto(employee?.photo || '');
  }, [employee?.photo]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const qrEmployee = useMemo(() => ({
    employeeId: employee?.employeeId || user?.employeeId,
    name: user?.name || employee?.name,
    eid: employee?.eid,
  }), [employee, user]);

  if (!open) return null;

  const handlePhotoClick = () => {
    if (isUploading) return;
    photoInputRef.current?.click();
  };

  const handlePhotoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user?.employeeId) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    setIsUploading(true);
    try {
      const photoBase64 = await compressImageFile(file);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update_my_profile_photo',
          employeeId: user.employeeId,
          username: user.username,
          portalAccountId: user.portalAccountId,
          photo: photoBase64,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!data) {
        alert('No response from server. Please try again or contact your administrator.');
        return;
      }
      if (data.status === 'success') {
        setPhoto(photoBase64);
        onPhotoUpdated?.();
      } else {
        alert(data.message || 'Profile photo could not be updated.');
      }
    } catch (error) {
      console.error('Profile photo update error:', error);
      alert('Server error. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return createPortal(
    <div className="employee-profile-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="employee-profile-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="employee-profile-title"
      >
        <header className="employee-profile-modal-header">
          <div>
            <p className="employee-profile-modal-eyebrow">Employee Portal</p>
            <h2 id="employee-profile-title" className="employee-profile-modal-title">My Profile</h2>
          </div>
          <button
            type="button"
            className="employee-profile-modal-close"
            onClick={onClose}
            aria-label="Close profile and return to attendance logs"
          >
            <CloseIcon />
          </button>
        </header>

        <div className="employee-profile-modal-body">
          <input
            type="file"
            ref={photoInputRef}
            accept="image/*"
            onChange={handlePhotoFileChange}
            style={{ display: 'none' }}
          />

          <div className="employee-profile-desktop-grid">
            <div className="employee-profile-info-panel">
              <div className="employee-profile-photo-section">
                <button
                  type="button"
                  className="employee-profile-photo-btn"
                  onClick={handlePhotoClick}
                  disabled={isUploading}
                  title="Update profile photo"
                >
                  <img
                    src={photo || 'https://via.placeholder.com/128?text=Photo'}
                    alt={user?.name || 'Employee'}
                    className="employee-profile-photo"
                  />
                  <span className="employee-profile-photo-overlay">
                    <CameraIcon />
                    <span>{isUploading ? 'Uploading' : 'Update Photo'}</span>
                  </span>
                </button>
                <div className="employee-profile-photo-copy">
                  <h3 className="employee-profile-name">{user?.name || 'Employee'}</h3>
                  <p className="employee-profile-role">Workforce Member</p>
                </div>
              </div>

              <div className="employee-profile-divider" />

              <div className="employee-profile-fields">
                <ProfileField label="Employee ID (EID)" value={employee?.eid} />
                <ProfileField label="RFID UID" value={employee?.employeeId || user?.employeeId} />
                <ProfileField label="Position" value={employee?.position} />
                <ProfileField label="Shift Schedule" value={getShiftLabel(employee?.shift)} />
                <ProfileField label="Portal Username" value={user?.username} />
              </div>

              <div className="employee-profile-actions">
                <button
                  type="button"
                  className="employee-profile-primary-btn"
                  onClick={handlePhotoClick}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Update Profile Photo'}
                </button>
                <button type="button" className="employee-profile-secondary-btn" onClick={onClose}>
                  Close
                </button>
              </div>
            </div>

            {qrEmployee.employeeId && (
              <aside className="employee-profile-qr-panel">
                <div className="employee-profile-qr-wrap">
                  <h4 className="employee-profile-qr-heading">My Backup QR Code</h4>
                  <EmployeeQRCode employee={qrEmployee} />
                </div>
              </aside>
            )}
          </div>
        </div>

        <footer className="employee-profile-mobile-footer">
          <button type="button" className="employee-profile-back-btn" onClick={onClose}>
            <CloseIcon />
            Back to Attendance Logs
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

export default EmployeeProfile;
